import React, { useEffect, useMemo, useRef, useState } from 'react';
import copy from '../datos/journey1-web.json';
import j1screens from '../datos/journey1.json';
import { session } from '../engine/store';
import { SINONIMOS, norm, tileByMomentoCode } from '../engine/momentos';
import { MARCAS, hashConMarca, useMarca } from '../engine/marcas';
import { Header } from '../components/Header';
import { ProtectionRing } from '../components/ProtectionRing';
import { momentoIcon, tileIcon, IconArrowRight, IconChat, IconShield, IconSearch } from '../components/icons';

const { hero } = copy;
const momentoTiles = j1screens.find((s) => s.key === 'momento').tiles;

// Reveal al hacer scroll: marca .reveal con .in cuando entra al viewport
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const targets = el.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      targets.forEach((t) => t.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    targets.forEach((t) => io.observe(t));
    return () => io.disconnect();
  }, []);
  return ref;
};

// Buscador de momentos en la landing: al elegir, entra al wizard con el momento ya puesto
function MomentoSearch({ onPick }) {
  const [q, setQ] = useState('');
  const sugerencias = useMemo(() => {
    const nq = norm(q.trim());
    if (!nq) return [];
    return SINONIMOS.filter((s) => norm(s.t).includes(nq)).slice(0, 6);
  }, [q]);
  return (
    <div className="momento-search" data-testid="landing-momento-picker">
      <span className="momento-search-k" data-testid="landing-search-label">¿Otro momento? Escríbelo aquí</span>
      <div className="momento-search-box">
        <IconSearch size={18} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Escribe tu momento… (p. ej. “me mudé”, “nació mi hijo”)"
          data-testid="landing-momento-search"
          autoComplete="off"
        />
      </div>
      {sugerencias.length ? (
        <ul className="momento-tips" data-testid="landing-momento-tips">
          {sugerencias.map((s, i) => {
            const tile = tileByMomentoCode(momentoTiles, s.v);
            const Icon = tileIcon('momento', tile.v);
            return (
              <li key={s.t}>
                <button type="button" onClick={() => onPick(tile)} data-testid={`landing-momento-tip-${i}`}>
                  <Icon size={18} /> <span>{s.t}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : q.trim() ? (
        <p className="momento-nohit" data-testid="landing-momento-nohit">
          No lo tengo en la lista, pero un asesor te ayuda. Elige “Otra cosa”.
        </p>
      ) : null}
    </div>
  );
}

export default function Landing({ onStart }) {
  const [scrolled, setScrolled] = useState(false);
  const [marcaHome, setMarcaHome] = useMarca();
  const rootRef = useReveal();

  // Navegar a la experiencia B2B2C (micrositio multimarca / vista corredor) sin resetear la sesión.
  const irB2B = (vista) => { window.location.hash = hashConMarca(vista, marcaHome.id); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const pickMomento = (tile) => {
    // sobrevive al resetSession() de onStart: el wizard lo lee al montar
    session.prefillMomento = {
      momento: tile.v,
      go: tile.go || null,
      caso: ['A', 'B', 'C'].includes(tile.go) ? tile.go : null,
    };
    onStart('#J1');
  };

  return (
    <div className="landing" ref={rootRef} data-testid="landing">
      <Header className={scrolled ? 'scrolled' : ''} onHome={() => {}}>
        <button type="button" className="btn-ghost" onClick={() => onStart('#A')} data-testid="cta-header-chat">
          <IconChat size={16} /> Abrir el chat
        </button>
      </Header>

      {/* Entrada a la experiencia B2B2C multimarca desde la home */}
      <div className="b2b-bar" data-testid="home-b2b" style={{ '--marca': marcaHome.color }}>
        <span className="b2b-bar-k">Para entidades · la plataforma con tu marca</span>
        <label className="b2b-entidad">
          <span>Entidad</span>
          <select value={marcaHome.id} onChange={(e) => setMarcaHome(e.target.value)} data-testid="home-entidad-select">
            {Object.values(MARCAS).map((m) => (
              <option key={m.id} value={m.id}>{m.nombre}</option>
            ))}
          </select>
        </label>
        <button type="button" className="b2b-bar-btn" style={{ background: marcaHome.color }} onClick={() => irB2B('sitio')} data-testid="home-ir-sitio">
          Sitio del cliente
        </button>
        <button type="button" className="b2b-bar-btn ghost" onClick={() => irB2B('corredor')} data-testid="home-ir-corredor">
          Vista del corredor
        </button>
      </div>

      <section className="hero" data-testid="landing-hero">
        <div className="hero-copy">
          <h1>{hero.titular}</h1>
          <p className="hero-sub">{hero.subtitulo}</p>
          <div className="hero-ctas">
            <button type="button" className="btn-accion" onClick={() => onStart('#J1')} data-testid="cta-primario">
              {hero.cta_primario}
            </button>
            <button type="button" className="btn-ghost" onClick={() => onStart('#A')} data-testid="cta-secundario">
              {hero.cta_secundario} <IconArrowRight size={15} />
            </button>
          </div>
          <p className="hero-nota">{hero.nota}</p>
        </div>
        <div className="hero-ring">
          <ProtectionRing filled={0} size={260}>
            <span className="ring-empty">Tu mapa se arma con cinco respuestas</span>
          </ProtectionRing>
        </div>
      </section>

      <section className="momentos" data-testid="landing-momentos">
        <h2>Momentos que sabemos acompañar</h2>
        <div className="momentos-grid">
          {hero.tarjeta_momentos.map(([emoji, t, d], i) => {
            const Icon = momentoIcon[emoji] || IconShield;
            return (
              <button
                type="button"
                className="momento-card reveal"
                style={{ transitionDelay: `${i * 60}ms` }}
                key={i}
                onClick={() => onStart('#J1')}
                data-testid={`momento-card-${i}`}
              >
                <Icon size={26} />
                <strong>{t}</strong>
                <span>{d}</span>
              </button>
            );
          })}
        </div>
        <MomentoSearch onPick={pickMomento} />
      </section>
    </div>
  );
}
